import { Inject, inject, Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  CollectionReference,
  deleteDoc,
  doc,
  docData,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  setDoc,
  writeBatch
} from '@angular/fire/firestore';
import { LoggerService } from './logger.service';
import { map } from 'rxjs';

type StringKeys<T> = {
  [K in keyof T]: T[K] extends string ? K : never
}[keyof T];

/**
 * **How to Use Firestore Core Service**
 * 
 * This guide outlines the steps for configuring and using the `FirestoreCoreService` in your Angular application.
 * 
 * **1. Define Injection Tokens & Paths for strict typings**
 * 
 * If you need multiple configurations or paths, create injection tokens to handle different 
 * collection paths and ID fields.
 * 
 * **Example:**
 * ```typescript
 * import { InjectionToken } from '@angular/core';
 * import { User } from 'types';
 * import { FirestoreCoreService } from '../services/firestore-core.service';
 * 
 * export type CollectionPath = 'users' | 'merchants';
 *
 * // Injection token for the Firestore service handling User documents
 * export const USER_FIRESTORE_SERVICE =
 *   new InjectionToken<FirestoreCoreService<User, CollectionPath>>('UserFirestoreService');
 * 
 * ```
 * 
 * **2. Configure the Service**
 * 
 * Provide the `FirestoreCoreService` in your `app.config.ts` with the necessary collection path and ID field. 
 * This configuration sets up the service for use throughout your application.
 * 
 * **Example:**
 * ```typescript
 * import { ApplicationConfig } from '@angular/core';
 * import { USER_FIRESTORE_SERVICE, CollectionPath } from './fire-core.tokens'; // Adjust the path as necessary
 * import { FirestoreCoreService } from './services/firestore-core.service'; // Adjust the path as necessary
 * import { User } from 'types'; // Adjust the path as necessary
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     { 
 *       provide: USER_FIRESTORE_SERVICE, 
 *       useFactory: () => new FirestoreCoreService<User, CollectionPath>('users', 'uid') 
 *     },
 *   ]
 * };
 * ```
 * 
 * **3. Inject and Use the Service**
 * 
 * Inject the configured `FirestoreCoreService` into your components or services using the injection token you created. 
 * Utilize its methods to perform operations on Firestore, such as fetching or updating documents.
 * 
 * **Example:**
 * ```typescript
 * import { Component, inject } from '@angular/core';
 * import { USER_FIRESTORE_SERVICE } from './fire-core.tokens'; // Adjust the path as necessary
 * import { FirestoreCoreService } from './services/firestore-core.service'; // Adjust the path as necessary
 * import { User } from 'types'; // Adjust the path as necessary
 * 
 * @Component({
 *   selector: 'app-user-component',
 *   templateUrl: './user-component.component.html',
 *   standalone: true,
 * })
 * export class UserComponent {
 *   // Inject the FirestoreCoreService configured for User documents
 *   private userService = inject(USER_FIRESTORE_SERVICE);
 * 
 *   async getUser(userId: string) {
 *     try {
 *       const data = await this.userService.ref.snapshot.getById(userId).toPromise();
 *       console.log(data);
 *       return data;
 *     } catch (error) {
 *       console.error('Error fetching user:', error);
 *       throw error;
 *     }
 *   }
 * 
 *   async setUser(user: User) {
 *     try {
 *       const data = await this.userService.ref.set(user).toPromise();
 *       console.log('User saved:', data);
 *       return data;
 *     } catch (error) {
 *       console.error('Error saving user:', error);
 *       throw error;
 *     }
 *   }
 * }
 * ```
 * 
 * **Notes:**
 * - Ensure that the collection path (`'users'`) and ID field (`'uid'`) match your Firestore setup.
 * - Use the provided methods to interact with Firestore, including handling errors 
 * and processing responses appropriately.
 */

@Injectable({ providedIn: 'root' })
export class FirestoreCoreService<TData extends object, TPaths extends string> {
  private firestore = inject(Firestore);
  private logger = inject(LoggerService);

  protected collectionPath!: TPaths;
  protected idField: StringKeys<TData>;

  constructor(
    @Inject(String) _collectionPath: TPaths,
    @Inject(String) _idField: StringKeys<TData>,

  ) {
    this.collectionPath = _collectionPath;
    this.idField = _idField
  }

  get ref() {
    return {
      fetchAll: () => {
        return this.fetchAll();
      },
      fetchById: (id: string) => {
        return this.fetchById(id)
      },
      snapshot: {
        fetchAll: () => {
          return this.fetchAllSnapshot();
        },
        fetchById: (id: string) => {
          return this.fetchByIdSnapshot(id)
        }
      },
      save: (data: Partial<TData>) => {
        return this.save(data);
      },
      remove: (id: string) => {
        return this.remove(id);
      },
      batch: this.createBatch(),
    };
  }

  private fetchAll() {
    const collectionRef = collection(this.firestore, this.collectionPath) as CollectionReference<TData>
    return collectionData(collectionRef, { idField: this.idField })
      .pipe(map(data => data as TData[]));
  }

  private async fetchAllSnapshot() {
    try {
      const collectionRef = collection(this.firestore, this.collectionPath) as CollectionReference<TData>
      const snapshot = await getDocs(collectionRef);
      return snapshot
        .docs.map(
          doc => {
            return { [this.idField]: doc.id, ...doc.data() } as TData
          }
        );
    } catch (error) {
      this.logger.log(`Error fetching documents: ${error} `, 'error');
      throw error;
    }
  }

  private fetchById(id: string) {
    const collectionRef = collection(this.firestore, this.collectionPath) as CollectionReference<TData>
    const docRef = doc(collectionRef, id);
    return docData(docRef, { idField: this.idField })
      .pipe(map(data => {
        const _data = data ? data : null;
        return _data;
      }));
  }

  private async fetchByIdSnapshot(id: string) {
    try {
      const docRef = doc(this.firestore, `${this.collectionPath}/${id}`) as DocumentReference<TData | undefined>;
      const snapshot = await getDoc(docRef);
      const data = snapshot.data();
      if (!data) {
        return null;
      }

      return { [this.idField]: snapshot.id, ...data } as TData;
    } catch (error) {
      this.logger.log(`Error fetching document by ID: ${error}`, 'error');
      throw error;
    }
  }

  private async save(data: Partial<TData>) {
    try {
      const docRef = this.idField in data ?
        doc(this.firestore, `${this.collectionPath}/${data[this.idField]}`) as DocumentReference<TData> :
        doc(collection(this.firestore, this.collectionPath)) as DocumentReference<TData>;

      const saveData = this.idField in data
        ? (() => {
          const { [this.idField]: _, ...omittedData } = data;
          return omittedData;
        })()
        : { ...data };

      await setDoc(docRef, saveData);
      this.logger.log('Document successfully written!');
      return { [this.idField]: docRef.id, ...data } as TData;
    } catch (error) {
      this.logger.log(`Error writing document: ${error}`, 'error');
      throw error;
    }
  }

  private async remove(id: string) {
    try {
      const docRef = doc(this.firestore, `${this.collectionPath}/${id}`);
      await deleteDoc(docRef);
      this.logger.log('Item deleted successfully.');
    } catch (error) {
      this.logger.log(`Error deleting item: ${error}`, 'error');
      throw error;
    }
  }

  //#region -> Batch Helper
  private createBatch() {
    const batch = writeBatch(this.firestore);

    return {

      save: (data: Partial<TData>) => {
        const docRef = this.idField in data ?
          doc(this.firestore, `${this.collectionPath}/${data[this.idField]}`) as DocumentReference<TData> :
          doc(collection(this.firestore, this.collectionPath)) as DocumentReference<TData>;

        const saveData = this.idField in data
          ? (() => {
            const { [this.idField]: _, ...omittedData } = data;
            return omittedData;
          })()
          : { ...data };

        batch.set(docRef, saveData);
        return this;
      },
      delete: (id: string) => {
        const docRef = doc(this.firestore, `${this.collectionPath}/${id}`);
        batch.delete(docRef);
        return this;
      },
      commit: async () => {
        try {
          await batch.commit();
          this.logger.log('Batch committed successfully.');
        } catch (error) {
          this.logger.log(`Error committing batch: ${error}`, 'error');
          throw error;
        }
      }
    };
  }

  //#endregion
}